<template>
    <div>
        <div
            class="image-compare-viewer"
            :id="`image-compare-${uuid}`"
        >
            <img :src="props.images[0].path" alt="" draggable="false">
            <img :src="props.images[1].path" alt="" draggable="false">
        </div>
    </div>
</template>

<script setup lang="ts">
    import ImageCompare from "image-compare-viewer"
    import { v4 as uuidv4 } from 'uuid'

    const props = defineProps<{
        images: any
    }>();

    const uuid = uuidv4().slice(0, 6)

    const options = {
        // UI Theme Defaults
        controlColor: "#00ff00",
        controlShadow: false,
        addCircle: true,
        addCircleBlur: false,

        // Label Defaults
        showLabels: false,
        labelOptions: {
            before: '',
            after: '',
            onHover: false
        },

        // Smoothing
        smoothing: true,
        smoothingAmount: 100,

        // Other options
        hoverStart: false,
        verticalMode: false,
        startingPoint: 50,
        fluidMode: false
    };

    onMounted(() => {
        const element = document.getElementById(`image-compare-${uuid}`)

        const viewer = new ImageCompare(element, options).mount()
    })
</script>

<style>
    .icv {
        position: relative;
        overflow: hidden;
        cursor: row-resize;

        &.icv__icv--vertical {
            cursor: row-resize;
        }

        &.icv__icv--horizontal {
            cursor: col-resize;
        }

        .icv__img {
            pointer-events: none;
            -khtml-user-select: none;
            -o-user-select: none;
            -moz-user-select: none;
            -webkit-user-select: none;
            user-select: none;
            max-width: none;
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            border: 0 !important;
            border-radius: 0 !important;
            top: 0;
            display: block;
        }

        .icv__img-a {
            height: auto;
            position: static;
            z-index: 1;
            left: 0px;
        }

        .icv__img-b {
            height: 100%;
            position: absolute;
            z-index: 2;
            left: auto;
            right: 0px;
            width: auto;
        }

        &.icv__icv--vertical .icv__img-b {
            width: 100%;
            height: auto;
        }

        .icv__imposter {
            z-index: 4;
            position: absolute;
            top: 0px;
            left: 0px;
            width: 100%;
            height: 100%;
        }

        .icv__wrapper {
            position: absolute;
            width: 100%;
            height: 100%;
            right: 0px;
            top: 0px;
            overflow: hidden;
            background-size: cover;
            background-position: center center;
            z-index: 3;
        }

        &.icv__is--fluid .icv__wrapper,
        &.icv__icv--vertical .icv__wrapper {
            width: 100% !important;
        }

        &.icv__is--fluid .icv__wrapper,
        &.icv__icv--horizontal .icv__wrapper {
            height: 100% !important;
        }

        .icv__fluidwrapper {
            background-size: cover;
            background-position: center;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
        }

        .icv__control {
            position: absolute;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            box-sizing: border-box;
            height: 100%;
            top: 0px;
            z-index: 5;
        }

        &.icv__icv--vertical .icv__control {
            flex-direction: row;
            left: 0;
            width: 100%;
        }

        .icv__control-line {
            height: 50%;
            width: 2px;
            z-index: 6;
        }

        &.icv__icv--vertical .icv__control-line {
            width: 50%;
        }

        .icv__theme-wrapper {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: absolute;
            transition: all 0.1s ease-out 0s;
            z-index: 5;

            path {
                stroke: var(--color-dark-blue);
                stroke-width: 2;
            }
        }

        &.icv__icv--vertical .icv__theme-wrapper {
            flex-direction: column;
        }

        .icv__arrow-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            transition: all 0.1s ease-out 0s;
        }

        .icv__arrow-a {
            transform: scale(1.5) rotateZ(180deg);
            height: 20px;
            width: 20px;
            -webkit-filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.33));
            filter: drop-shadow(0px -3px 5px rgba(0, 0, 0, 0.33));
        }
        .icv__arrow-b {
            transform: scale(1.5) rotateZ(0deg);
            height: 20px;
            width: 20px;

            -webkit-filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.33));
            filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.33));
        }

        .icv__circle {
            width: 50px;
            height: 50px;
            -webkit-box-sizing: border-box;
            box-sizing: border-box;
            -ms-flex-negative: 0;
            flex-shrink: 0;
            border-radius: 999px;
            background-color: var(--color-green);
            transition: all .3s ease;
        }

        &:hover {
            .icv__circle {
                box-shadow: 0 0 6px 5px var(--color-green);
            }
        }

        .icv__label {
            position: absolute;
            bottom: 1rem;
            z-index: 12;
            background: rgba(0, 0, 0, 0.33);
            color: white;
            border-radius: 3px;
            padding: 0.5rem 0.75rem;
            font-size: 0.85rem;
            user-select: none;
        }

        .icv__label.vertical {
            bottom: auto;
            left: 1rem;
        }

        .icv__label.on-hover {
            transform: scale(0);
            transition: 0.25s cubic-bezier(0.68, 0.26, 0.58, 1.22);
        }

        &:hover .icv__label.on-hover {
            transform: scale(1);
        }

        .icv__label-before {
            left: 1rem;
        }
        .icv__label-after {
            right: 1rem;
        }
        .icv__label-before.vertical {
            top: 1rem;
        }
        .icv__label-after.vertical {
            bottom: 1rem;
            right: auto;
        }
    }

    .icv__body {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        padding-right: 0 !important;
    }
</style>
